import { useCallback } from "react";
import { useOrgPublicKey } from "~/hooks/useOrgEncryption";
import { useEncryption, base64ToUint8Array } from "~/hooks/useEncryption";
import {
    generatePerReportDek,
    wrapDekForUser,
    bufferToBase64,
    unwrapDekFromUser,
} from "~/helpers/envelopeCrypto";
import { importRsaPublicKey, rsaOaepWrapDek } from "~/helpers/orgCrypto";
import { usePassword } from "~/store/credentials";
import type { EncryptedPayload } from "~/types/global";
import { CRYPTO_SERVICE, type CryptoResult } from "~/workers/crypto";

export function useEnvelopeEncryption() {
    const { data: orgPublicKey } = useOrgPublicKey();
    const { _encryptData, decryptData, deriveKey } = useEncryption();
    const secretKey = usePassword();

    const encryptReportWithEnvelope = useCallback(
        async (plaintext: Record<string, any>) => {
            if (!orgPublicKey) {
                return null;
            }

            const perReportDek = generatePerReportDek();
            const encryptedFields = await _encryptData(plaintext, perReportDek.buffer as ArrayBuffer);

            const userDek = base64ToUint8Array(secretKey || "");
            const rawKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
            const { user_wrapped_dek, user_dek_iv } = await wrapDekForUser(rawKey, perReportDek);

            const rsaPubKey = await importRsaPublicKey(orgPublicKey.public_key);
            const orgWrappedDekBuffer = await rsaOaepWrapDek(rsaPubKey, perReportDek);
            const org_wrapped_dek = bufferToBase64(new Uint8Array(orgWrappedDekBuffer));

            return {
                encryptedFields,
                user_wrapped_dek,
                user_dek_iv,
                org_wrapped_dek,
                key_version: orgPublicKey.key_version,
            };
        },
        [orgPublicKey, _encryptData, secretKey],
    );

    /**
     * Returns a decryptor function for an item that may use envelope encryption.
     * If the item has user_wrapped_dek, unwraps the per-item DEK with the user's key
     * and decrypts fields using CRYPTO_SERVICE (WASM), matching the report decryption flow.
     * Falls back to the legacy decryptData path for items without envelope fields.
     */
    const createItemDecryptor = useCallback(
        (item: { user_wrapped_dek?: string | null; user_dek_iv?: string | null }) => {
            if (!item.user_wrapped_dek || !item.user_dek_iv) {
                return decryptData;
            }

            let cachedDek: Uint8Array | null = null;

            return async (encryptedField: EncryptedPayload): Promise<CryptoResult<any>> => {
                try {
                    if (!cachedDek) {
                        const userDek = await deriveKey();
                        const rawKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
                        cachedDek = await unwrapDekFromUser(rawKey, item.user_wrapped_dek!, item.user_dek_iv!);
                    }

                    const ciphertext = base64ToUint8Array(encryptedField.encrypted);
                    const iv = base64ToUint8Array(encryptedField.iv);
                    const { decrypted } = await CRYPTO_SERVICE.decrypt(cachedDek, iv, ciphertext);
                    return { success: true, data: decrypted };
                } catch (error) {
                    return { success: false, error: error instanceof Error ? error.message : "Envelope decryption error" };
                }
            };
        },
        [decryptData, deriveKey],
    );

    return { encryptReportWithEnvelope, createItemDecryptor };
}
